import './AmcMediaMix.scss';
import { formatMoneyAsInt } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { endOfMonth, format, parseISO, startOfMonth, subMonths } from 'date-fns';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { abbreviateNumber } from '@src/utils/data';
import { useSetError } from '@src/redux/modals';
import { AreaAndBarChart } from '@Components/AreaAndBarChart';
import { ChartContainer } from '@Components/ChartContainer';
import { BPMTable, Header } from '@Components/StickyTable/BPMTable';
import { CellRenderer } from '@Components/StickyTable/StickyTable';
import { Design } from '@Components/designConstants';
import { DatePicker } from '@Components/DatePicker';
import { DateRange } from '@Components/DatePicker/date-picker-types';
import {
  getNtbTableHeaders,
  getProspectiveTableHeaders,
  MediaMixMaxValues
} from './amc-media-mix-config';
import { AmcMediaMixTableRow } from './amc-media-mix-types';
import { useAmcMediaMixData } from './utils/useAmcMediaMixData';

const GRAND_TOTAL_MEDIA_TYPE = 'Grand Total';
const DATE_FORMAT = 'yyyy-MM-dd';

// Previous month's report isn't prepared until the 15th of the current month.
const monthsBack = new Date().getDate() < 15 ? 2 : 1;

const DEFAULT_DATE_RANGE: DateRange = {
  start: format(startOfMonth(subMonths(new Date(), monthsBack + 2)), DATE_FORMAT),
  end: format(startOfMonth(subMonths(new Date(), monthsBack)), DATE_FORMAT)
};

const NoMediaMixData = () => <div className='amc-media-mix-no-data'>No data to show</div>;

const totalsRenderer: CellRenderer<Element | number | string | undefined> = ({
  data,
  style,
  classes
}) => (
  <div className={classes.join(' ')} style={style}>
    {typeof data === 'string' ? data : abbreviateNumber(Number(data))}
  </div>
);

const getMax = (rows: AmcMediaMixTableRow[], key: 'reach' | 'purchased' | 'avgOrderSize') =>
  Math.max(...rows.map(row => (row.mediaType === GRAND_TOTAL_MEDIA_TYPE ? 0 : Number(row[key]))));

// The Grand Total row's rate/money fields are pre-formatted to strings here (matching what the
// table's per-row renderer does for that same row — see amc-media-mix-config) so BPMTable's
// generic totalsRenderer above can just check typeof rather than re-deriving the formatting.
const getFormattedTotals = (rows: AmcMediaMixTableRow[]): AmcMediaMixTableRow | undefined => {
  const totals = rows.find(row => row.mediaType === GRAND_TOTAL_MEDIA_TYPE);
  if (!totals) return undefined;
  return {
    ...totals,
    avgOrderSize: formatMoneyAsInt(Number(totals.avgOrderSize)),
    purchaseRate: `${totals.purchaseRate}%`
  };
};

interface MediaMixSectionProps {
  question: ReactNode;
  isBusy: boolean;
  tableRows: AmcMediaMixTableRow[];
  headers: Header[];
  graphData: Record<string, unknown>[];
}

const MediaMixSection = ({
  question,
  isBusy,
  tableRows,
  headers,
  graphData
}: MediaMixSectionProps) => (
  <ChartContainer title={question} isLoading={isBusy} minHeight={400}>
    <div className='amc-media-mix-content'>
      <div className='amc-media-mix-table-wrapper'>
        <BPMTable
          data={tableRows.filter(row => row.mediaType !== GRAND_TOTAL_MEDIA_TYPE)}
          filterBar={false}
          headers={headers}
          noRowsRenderer={NoMediaMixData}
          totals={getFormattedTotals(tableRows)}
          totalsRenderer={totalsRenderer}
        />
      </div>
      <div className='amc-media-mix-graph-wrapper'>
        <AreaAndBarChart
          areaDataKey='purchased'
          barDataKey='reach'
          data={graphData}
          xAxisDataKey='date'
          barYAxisDataKey='reach'
          barYAxisLabel='Shoppers Reached'
          areaYAxisDataKey='purchased'
          areaYAxisLabel='Shoppers Purchased'
        />
      </div>
    </div>
  </ChartContainer>
);

interface AmcMediaMixProps {
  amcInstanceId?: string;
}

export const AmcMediaMix = ({ amcInstanceId }: AmcMediaMixProps) => {
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const setError = useSetError();

  const {
    data: mediaMixData,
    isLoading,
    isFetching,
    error
  } = useAmcMediaMixData(
    amcInstanceId,
    dateRange.start,
    format(endOfMonth(parseISO(dateRange.end)), DATE_FORMAT)
  );

  useEffect(() => {
    if (error) {
      setError({
        title: 'Error fetching Media Mix data',
        message: 'There was an error fetching the Media Mix data. Please try again later.'
      });
    }
  }, [error, setError]);

  const maxValues = useMemo<MediaMixMaxValues>(() => {
    const prospectiveTable = mediaMixData?.prospectiveTable ?? [];
    const newToBrandTable = mediaMixData?.newToBrandTable ?? [];
    return {
      reach: getMax(prospectiveTable, 'reach'),
      proPurchased: getMax(prospectiveTable, 'purchased'),
      proPurchaseRate: Math.max(...prospectiveTable.map(row => Number(row.purchaseRate)), 0),
      proOrderSize: getMax(prospectiveTable, 'avgOrderSize'),
      ntbPurchased: getMax(newToBrandTable, 'purchased'),
      ntbPurchaseRate: Math.max(...newToBrandTable.map(row => Number(row.purchaseRate)), 0),
      ntbOrderSize: getMax(newToBrandTable, 'avgOrderSize')
    };
  }, [mediaMixData]);

  const isBusy = isFetching || isLoading;

  return (
    <div className='amc-media-mix'>
      <div className='amc-media-mix-header'>
        <DatePicker
          range={dateRange}
          onChange={setDateRange}
          fullMonthsOnly
          fiscalCalOverride={{}}
          design={Design.NEUTRAL}
        />
      </div>

      <MediaMixSection
        question={
          <span className='amc-media-mix-question'>
            Which media mix is most effective in converting{' '}
            <b className='amc-media-mix-emphasis'>prospective</b> shoppers into customers?
          </span>
        }
        isBusy={isBusy}
        tableRows={mediaMixData?.prospectiveTable ?? []}
        headers={getProspectiveTableHeaders(maxValues)}
        graphData={mediaMixData?.prospectiveGraph ?? []}
      />

      <MediaMixSection
        question={
          <span className='amc-media-mix-question'>
            Which media mix is most effective in converting{' '}
            <b className='amc-media-mix-emphasis'>new-to-brand</b> shoppers into customers?
          </span>
        }
        isBusy={isBusy}
        tableRows={mediaMixData?.newToBrandTable ?? []}
        headers={getNtbTableHeaders(maxValues)}
        graphData={mediaMixData?.newToBrandGraph ?? []}
      />
    </div>
  );
};
